import gsap from "gsap";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import NavHeight from "../utils/nav-height";

gsap.registerPlugin(ScrollToPlugin);

export default class Ajax {
    constructor() {
        this.navHeightInstance = new NavHeight();
        this.postsContainer = document.querySelector('.js-posts-container');
        if (!this.postsContainer) return;

        this.init();
    }

    /* ======================
     * INIT
     * ====================== */
    init() {
        this.initCategoryFilter();   // checkboxes desktop OU fallback liens
        this.initCategoriesSelect(); // <select> simple ou multiple
        this.initOrderbySelect();
        this.initPaginationListener();
        this.handleInitialLoadFromUrl();
    }

    /* ======================
     * HELPERS URL / STATE
     * ====================== */
    parseCatsFromQuery() {
        const raw = new URLSearchParams(window.location.search).get('categorie') || '';
        return raw
            .split(',')
            .map(s => s.trim())
            .filter(Boolean);
    }

    serializeCats(cats) {
        const arr = Array.isArray(cats) ? cats : String(cats || '').split(',');
        const unique = [...new Set(arr.map(s => s.trim()).filter(Boolean))];
        return unique.length ? unique.join(',') : '';
    }

    setQuery({ cats = [], orderby = '', paged = 1 }) {
        const params = new URLSearchParams(window.location.search);
        const csv = this.serializeCats(cats);
        if (csv) params.set('categorie', csv); else params.delete('categorie');
        if (orderby) params.set('orderby', orderby); else params.delete('orderby');
        if (paged) params.set('paged', String(paged)); else params.delete('paged');
        history.pushState(null, '', `?${params.toString()}`);
    }

    getCurrentOrderby() {
        const select = document.querySelector('.js-orderby-select');
        return select ? (select.value || 'date_DESC') : 'date_DESC';
    }

    /* ======================
     * UI SYNC
     * ====================== */
    updateNavCurrent(slugsArr = []) {
        const nav = document.querySelector('.js-categories-nav');
        if (!nav) return;

        const selected = Array.isArray(slugsArr) ? slugsArr : [];
        nav.querySelectorAll('.categories-nav_link').forEach(li => li.classList.remove('current'));

        if (selected.length === 0) {
            // « Tout voir » marqué actif si présent
            const first = nav.querySelector('.categories-nav_link');
            if (first) first.classList.add('current');
            // coche/décoche le checkbox « all » si on l’utilise
            const allToggle = nav.querySelector('.js-cat-toggle[value="__all"]');
            if (allToggle) allToggle.checked = true;
            nav.querySelectorAll('.js-cat-toggle:not([value="__all"])').forEach(cb => cb.checked = false);
            return;
        }

        // cases à cocher : marquer celles sélectionnées
        selected.forEach(slug => {
            const li = nav.querySelector(`.categories-nav_link[data-slug="${slug}"]`);
            if (li) li.classList.add('current');
        });

        // si checkboxes, décocher « all »
        const allToggle = nav.querySelector('.js-cat-toggle[value="__all"]');
        if (allToggle) allToggle.checked = false;

        // si ancienne nav en <a>, pas besoin de toucher (classes ajoutées ci-dessus)
    }

    syncCatsSelect(slugsArr = []) {
        const select = document.querySelector('.js-cats-select');
        if (!select) return;

        const isMultiple = select.hasAttribute('multiple');
        const set = new Set(Array.isArray(slugsArr) ? slugsArr : []);

        if (isMultiple) {
            const hasAny = set.size > 0;
            Array.from(select.options).forEach(opt => {
                if (opt.value === '__all') {
                    // En "Voir tout" on force __all = selected (état source de vérité côté mobile)
                    opt.selected = !hasAny;
                } else {
                    opt.selected = hasAny && set.has(opt.value);
                }
            });
        } else {
            // single (au cas où)
            select.value = set.size ? [...set][0] : 'all';
        }

        // Repeindre NiceSelect
        select.niceSelectInstance?.update?.();
    }


    /* ======================
     * INIT HANDLERS
     * ====================== */

    // Desktop : checkboxes si présents, sinon fallback liens .js-category-link
    initCategoryFilter() {
        // 1) Mode checkboxes (multi)
        const hasToggles = !!document.querySelector('.js-cat-toggle');
        if (hasToggles) {
            document.addEventListener('change', (event) => {
                const input = event.target.closest('.js-cat-toggle');
                if (!input) return;

                const orderby = this.getCurrentOrderby();

                // Clic sur « Tout voir » => reset
                if (input.value === '__all') {
                    // cocher all, décocher le reste
                    document.querySelectorAll('.js-cat-toggle').forEach(el => {
                        el.checked = (el === input);
                    });
                    this.setQuery({ cats: [], orderby, paged: 1 });
                    this.updateNavCurrent([]);
                    this.syncCatsSelect([]);
                    this.scrollToTopOfPosts();
                    this.fetchPosts([], 1, orderby);
                    return;
                }

                // Construire la liste des catégories cochées (hors all)
                const checked = Array.from(document.querySelectorAll('.js-cat-toggle'))
                    .filter(el => el.value !== '__all' && el.checked)
                    .map(el => el.value);

                // Maintenir l’état de « all »
                const all = document.querySelector('.js-cat-toggle[value="__all"]');
                if (all) all.checked = checked.length === 0;

                this.setQuery({ cats: checked, orderby, paged: 1 });
                this.updateNavCurrent(checked);
                this.syncCatsSelect(checked);
                this.scrollToTopOfPosts();
                this.fetchPosts(checked, 1, orderby);
            });
        }

        // 2) Fallback ancien mode : liens .js-category-link (mono)
        document.addEventListener('click', (event) => {
            const link = event.target.closest('.js-category-link');
            if (!link) return;
            // si on a des checkboxes, on ignore les liens (la nav a changé de structure)
            if (hasToggles) return;

            event.preventDefault();

            const isAll = link.hasAttribute('data-all')
                || link.getAttribute('href') === ''
                || !new URL(link.href, window.location.origin).searchParams.has('categorie');

            const category = isAll ? [] : [ this.getCategoryFromHref(link.href) ];
            const orderby  = this.getCurrentOrderby();

            this.setQuery({ cats: category, orderby, paged: 1 });
            this.updateNavCurrent(category);
            this.syncCatsSelect(category);
            this.scrollToTopOfPosts();
            this.fetchPosts(category, 1, orderby);
        });
    }

    // Mobile : <select> single OU multiple
    initCategoriesSelect() {
        const select = document.querySelector('.js-cats-select');
        if (!select) return;

        const isMultiple = select.hasAttribute('multiple');

        select.addEventListener('change', () => {
            const orderby = this.getCurrentOrderby();
            let cats = [];

            if (isMultiple) {
                const values = Array.from(select.selectedOptions).map(o => o.value);

                if (values.length === 0) {
                    // reset total (Voir tout)
                    cats = [];
                } else if (values.includes('__all')) {
                    // sécurité: si __all + autres (cas limite), ne garder que les autres
                    const others = values.filter(v => v !== '__all');
                    cats = others.length ? others : [];
                } else {
                    cats = values;
                }
            } else {
                const v = select.value || 'all';
                cats = (v === 'all') ? [] : [v];
            }

            this.setQuery({ cats, orderby, paged: 1 });
            this.updateNavCurrent(cats);
            this.scrollToTopOfPosts();
            this.fetchPosts(cats, 1, orderby);
        });
    }

    initOrderbySelect() {
        const select = document.querySelector('.js-orderby-select');
        if (!select) return;

        select.addEventListener('change', () => {
            const orderbyParam = select.value;
            const effective    = orderbyParam || 'date_DESC';

            const cats = this.parseCatsFromQuery();

            this.setQuery({ cats, orderby: orderbyParam, paged: 1 });
            this.scrollToTopOfPosts();
            this.fetchPosts(cats, 1, effective);
        });
    }

    initPaginationListener() {
        this.postsContainer.addEventListener('click', e => {
            const link = e.target.closest('a.pagination-link');
            if (!link) return;

            const isDisabled = link.classList.contains('disabled') || link.getAttribute('aria-disabled') === 'true';
            if (isDisabled) { e.preventDefault(); return; }

            e.preventDefault();

            const url  = new URL(link.href, window.location.origin);
            let page   = url.searchParams.get('paged') || 1;

            if (!url.searchParams.has('paged')) {
                const match = url.pathname.match(/\/page\/(\d+)/);
                if (match) page = match[1];
            }

            const currentParams = new URLSearchParams(window.location.search);
            const csv = url.searchParams.get('categorie') ?? currentParams.get('categorie') ?? '';
            const cats = csv.split(',').map(s => s.trim()).filter(Boolean);
            const orderby = url.searchParams.get('orderby') ?? currentParams.get('orderby') ?? 'date_DESC';

            this.setQuery({ cats, orderby, paged: page });
            this.scrollToTopOfPosts();
            this.fetchPosts(cats, page, orderby);
        });
    }

    /* ======================
     * FLOW
     * ====================== */
    handleInitialLoadFromUrl() {
        const params  = new URLSearchParams(window.location.search);
        const cats    = (params.get('categorie') || '').split(',').map(s => s.trim()).filter(Boolean);
        const hasOrd  = params.has('orderby');
        const orderby = hasOrd ? params.get('orderby') : 'date_DESC';
        const paged   = params.get('paged') || 1;

        this.updateNavCurrent(cats);

        const orderSelect = document.querySelector('.js-orderby-select');
        if (orderSelect) {
            orderSelect.value = hasOrd ? orderby : '';
            orderSelect.niceSelectInstance?.update?.();
        }

        this.syncCatsSelect(cats);
        this.fetchPosts(cats, paged, orderby);
    }

    scrollToTopOfPosts() {
        const block = document.querySelector('.block-posts');
        if (block) {
            const navOffset = this.navHeightInstance?.navHeightValue || 0;
            const responsiveOffset = window.innerWidth >= 1200 ? 100 : 50;
            gsap.to(window, {
                duration: 0.6,
                scrollTo: {
                    y: block,
                    offsetY: navOffset + responsiveOffset,
                },
                ease: 'power2.out'
            });
        }
    }

    /* ======================
     * AJAX
     * ====================== */
    fetchPosts(categories = [], paged = 1, orderby = 'date_DESC') {
        const catsCsv = Array.isArray(categories) ? this.serializeCats(categories) : String(categories || '');
        const params = new URLSearchParams();
        if (catsCsv) params.set('categorie', catsCsv);
        params.set('paged', String(paged));
        params.set('orderby', orderby);

        const url = `/ajax/filter-posts-by-category/?${params.toString()}`;

        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
            }
        })
            .then(response => {
                if (!response.ok) throw new Error(`Erreur ${response.status}`);
                return response.json();
            })
            .then(data => {
                if (data.success) {
                    this.postsContainer.innerHTML = data.data.html;

                    // côté PHP tu renvoies currentCatSlugs (array) si possible
                    const slugs = Array.isArray(data.data.currentCatSlugs)
                        ? data.data.currentCatSlugs
                        : (data.data.category ? String(data.data.category).split(',').filter(Boolean) : []);

                    this.updateNavCurrent(slugs);
                    this.syncCatsSelect(slugs);

                    gsap.from('.post-item', {
                        opacity: 0,
                        duration: 0.5,
                        stagger: 0.1,
                        ease: 'power2.out'
                    });
                } else {
                    console.error('Réponse invalide :', data);
                }
            })
            .catch(error => {
                console.error('Erreur AJAX :', error);
            });
    }

    /* ======================
     * Fallback utilitaire (mode liens)
     * ====================== */
    getCategoryFromHref(href) {
        const url = new URL(href, window.location.origin);
        return url.searchParams.get('categorie') || 'all';
    }
}
